import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, CheckCircle, Copy, ExternalLink, Eye, Globe2, RefreshCw, ShieldAlert, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import {
  PublicScanPageResponse,
  PublicScanProjectResponse,
  PublicScanStatus,
} from '../../types/application';

interface PublicScanPageSettingsProps {
  organizationId: string;
  applicationId: string;
  canManage: boolean;
}

const STATUS_STYLES: Record<PublicScanStatus, { label: string; className: string; icon: React.ElementType }> = {
  passing: {
    label: 'No vulnerabilities detected',
    className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    icon: CheckCircle,
  },
  failing: {
    label: 'Vulnerabilities detected',
    className: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    icon: ShieldAlert,
  },
  unknown: {
    label: 'Scan unknown',
    className: 'bg-neutral-500/10 text-ui-text-secondary border-ui-border-primary/20',
    icon: Activity,
  },
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return 'Not available';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return 'Not available';
  }
};

const formatScanType = (scanType: string): string =>
  scanType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const PublicScanPageSettings: React.FC<PublicScanPageSettingsProps> = ({
  organizationId,
  applicationId,
  canManage,
}) => {
  const api = useAuthenticatedApi();
  const [settings, setSettings] = useState<PublicScanPageResponse | null>(null);
  const [preview, setPreview] = useState<PublicScanProjectResponse | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<PublicScanPageResponse>(
        apiEndpoints.applications.publicScanPage(organizationId, applicationId)
      );
      setSettings(response);
      setDisplayName(response.page.display_name);
      setProjectSlug(response.page.project_slug);
      setDescription(response.page.description || '');

      const previewResponse = await api.get<PublicScanProjectResponse>(
        apiEndpoints.applications.publicScanPagePreview(organizationId, applicationId)
      );
      setPreview(previewResponse);
    } catch (err: any) {
      setError(err.message || 'Failed to load public scan page settings');
    } finally {
      setLoading(false);
    }
  }, [api, organizationId, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const snapshot = preview?.snapshot || settings?.page.snapshot || null;
  const statusStyle = useMemo(
    () => STATUS_STYLES[snapshot?.status || 'unknown'],
    [snapshot?.status]
  );
  const StatusIcon = statusStyle.icon;

  const save = async (isPublic?: boolean) => {
    if (!canManage) return;
    setSaving(true);
    try {
      const response = await api.put<PublicScanPageResponse>(
        apiEndpoints.applications.publicScanPage(organizationId, applicationId),
        {
          display_name: displayName.trim(),
          project_slug: projectSlug.trim().toLowerCase(),
          description: description.trim() || null,
          ...(isPublic !== undefined ? { is_public: isPublic } : {}),
        }
      );
      setSettings(response);
      const previewResponse = await api.get<PublicScanProjectResponse>(
        apiEndpoints.applications.publicScanPagePreview(organizationId, applicationId)
      );
      setPreview(previewResponse);
      toast.success(isPublic === true ? 'Public page published' : isPublic === false ? 'Public page unpublished' : 'Public page saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save public page');
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    if (!canManage) return;
    setRefreshing(true);
    try {
      const response = await api.post<PublicScanPageResponse>(
        apiEndpoints.applications.publicScanPageRefresh(organizationId, applicationId),
        {}
      );
      setSettings(response);
      const previewResponse = await api.get<PublicScanProjectResponse>(
        apiEndpoints.applications.publicScanPagePreview(organizationId, applicationId)
      );
      setPreview(previewResponse);
      toast.success('Snapshot refreshed');
    } catch (err: any) {
      toast.error(err.message || 'Failed to refresh snapshot');
    } finally {
      setRefreshing(false);
    }
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
        <p className="text-sm text-red-600 dark:text-red-400">{error || 'Settings unavailable'}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-3 text-sm font-medium text-brand-primary dark:text-brand-primaryDarkMode hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const isPublic = settings.page.is_public;

  return (
    <div className="space-y-6">
      {!canManage && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          Owner permissions are required to publish or edit this page.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <div className="space-y-4">
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Globe2 className="w-5 h-5 text-ui-text-secondary" />
                <h2 className="text-lg font-semibold text-ui-text-primary">Public Status</h2>
              </div>
              <button
                type="button"
                onClick={() => void save(!isPublic)}
                disabled={!canManage || saving}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isPublic
                    ? 'bg-neutral-200 text-ui-text-primary hover:bg-ui-background-tertiary dark:bg-ui-background-tertiary dark:text-white dark:hover:bg-ui-background-tertiary'
                    : 'bg-brand-primary text-black hover:bg-brand-primaryDark'
                }`}
              >
                {isPublic ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {isPublic ? 'Unpublish' : 'Publish'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-ui-text-primary">Project Name</span>
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  disabled={!canManage || saving}
                  className="mt-2 w-full rounded-lg border border-ui-border-secondary bg-ui-background-tertiary px-3 py-2 text-sm text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-60"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ui-text-primary">Project Slug</span>
                <input
                  value={projectSlug}
                  onChange={(event) => setProjectSlug(event.target.value)}
                  disabled={!canManage || saving}
                  className="mt-2 w-full rounded-lg border border-ui-border-secondary bg-ui-background-tertiary px-3 py-2 text-sm font-mono text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-60"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-ui-text-primary">Description</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={!canManage || saving}
                rows={3}
                className="mt-2 w-full rounded-lg border border-ui-border-secondary bg-ui-background-tertiary px-3 py-2 text-sm text-ui-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:opacity-60"
              />
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!canManage || saving}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-3 py-2 text-sm font-medium text-black hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Spinner size={16} tone="current" /> : <CheckCircle className="w-4 h-4" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => void refresh()}
                disabled={!canManage || refreshing}
                className="inline-flex items-center gap-2 rounded-lg border border-ui-border-secondary px-3 py-2 text-sm font-medium text-ui-text-primary hover:bg-ui-background-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshing ? <Spinner size={16} tone="current" /> : <RefreshCw className="w-4 h-4" />}
                Refresh
              </button>
              <a
                href={settings.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-ui-border-secondary px-3 py-2 text-sm font-medium text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open Page
              </a>
            </div>
          </div>

          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-ui-text-primary">Badge Markdown</h3>
              <button
                type="button"
                onClick={() => void copy(settings.badge_markdown, 'Badge Markdown')}
                className="inline-flex items-center gap-2 rounded-lg border border-ui-border-secondary px-2.5 py-1.5 text-xs font-medium text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy
              </button>
            </div>
            <pre className="overflow-x-auto rounded-lg bg-ui-background-primary p-3 text-xs text-ui-text-tertiary">
              <code>{settings.badge_markdown}</code>
            </pre>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-5">
            <div className="flex items-center justify-between gap-3">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${statusStyle.className}`}>
                <StatusIcon className="w-4 h-4" />
                {statusStyle.label}
              </div>
              <Eye className="w-4 h-4 text-ui-text-tertiary" />
            </div>

            {snapshot && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-ui-text-secondary">{snapshot.status_reason}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-ui-background-tertiary p-3">
                    <p className="text-xs text-ui-text-tertiary">Actionable</p>
                    <p className="mt-1 text-2xl font-bold text-ui-text-primary">{snapshot.actionable_findings_count}</p>
                  </div>
                  <div className="rounded-lg bg-ui-background-tertiary p-3">
                    <p className="text-xs text-ui-text-tertiary">Fresh For</p>
                    <p className="mt-1 text-2xl font-bold text-ui-text-primary">{snapshot.freshness_days}d</p>
                  </div>
                </div>
                <div className="text-xs text-ui-text-tertiary space-y-1">
                  <p>Evaluated {formatDateTime(snapshot.evaluated_at)}</p>
                  <p>Stale after {formatDateTime(snapshot.stale_at)}</p>
                </div>
              </div>
            )}
          </div>

          {snapshot && (
            <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-ui-text-primary">Coverage</h3>
              <div className="space-y-3">
                {snapshot.repositories.map((repo) => (
                  <div key={repo.repository} className="rounded-lg bg-ui-background-tertiary p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-ui-text-primary truncate">{repo.repository}</p>
                      <span className="text-xs text-ui-text-tertiary">{repo.branch}</span>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {repo.scans.map((scan) => (
                        <div key={scan.scan_type} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-ui-text-secondary">{formatScanType(scan.scan_type)}</span>
                          <span className={scan.status === 'succeeded' && scan.fresh ? 'text-emerald-500' : 'text-amber-500'}>
                            {scan.status === 'succeeded' && scan.fresh ? 'Fresh' : scan.message || scan.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default PublicScanPageSettings;
